import { useState } from 'react'
import { Download, Info, Pencil, Plus, Upload } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, ImportDialog, PageHeader, PermissionGate,
  StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import {
  useDepartments, useDesignations, useGrades, useLocations, usePositionMutations, usePositions,
} from '../api'
import type { PositionView } from '../types'

const EMPTY = {
  code: '', title: '', departmentId: '', designationId: '', gradeId: '', locationId: '',
  reportsToPositionId: '', keyPosition: false, status: '',
}

const STATUS_TONE: Record<string, 'success' | 'neutral' | 'warning'> = {
  VACANT: 'warning',
  FILLED: 'success',
  FROZEN: 'neutral',
  CLOSED: 'neutral',
}

/**
 * The headcount plan.
 *
 * <p>Positions have been readable since the workforce migration and writable from nowhere:
 * one GET, no screen, no create. Every installation's headcount plan was whatever a migration
 * put there on day one, which is why nobody used it.
 *
 * <p>The distinction this screen exists to keep visible is between a <b>seat</b> and the
 * <b>person</b> in it. A position is closed, never deleted, because a requisition raised
 * against it and an assignment that named it both keep referring to it; and it cannot be
 * closed while somebody holds it, because that leaves an employment pointing at a seat the org
 * chart says is not there — an error invisible until somebody runs a headcount report.
 */
export function PositionsPage() {
  const breadcrumb = useBreadcrumb()
  const positions = usePositions()
  const departments = useDepartments()
  const designations = useDesignations()
  const grades = useGrades()
  const locations = useLocations()
  const { save, deactivate } = usePositionMutations()

  const [draft, setDraft] = useState<(typeof EMPTY & { id?: string }) | null>(null)
  const [removing, setRemoving] = useState<PositionView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)

  function open(position?: PositionView) {
    setError(null)
    setDraft(position
      ? {
          id: position.id,
          code: position.code,
          title: position.title,
          departmentId: position.departmentId ?? '',
          designationId: '',
          gradeId: '',
          locationId: '',
          reportsToPositionId: '',
          keyPosition: position.keyPosition,
          // FILLED and CLOSED are consequences, not choices, so the form never offers them.
          status: position.status === 'FROZEN' ? 'FROZEN' : 'VACANT',
        }
      : { ...EMPTY })
  }

  async function submit() {
    if (!draft) return
    setError(null)
    try {
      await save.mutateAsync({
        id: draft.id,
        code: draft.code,
        title: draft.title,
        departmentId: draft.departmentId || null,
        designationId: draft.designationId || null,
        gradeId: draft.gradeId || null,
        locationId: draft.locationId || null,
        reportsToPositionId: draft.reportsToPositionId || null,
        keyPosition: draft.keyPosition,
        status: draft.status || null,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That position could not be saved.')
    }
  }

  async function close() {
    if (!removing) return
    setError(null)
    try {
      await deactivate.mutateAsync(removing.id)
      setRemoving(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That position could not be closed.')
    }
  }

  const openSeats = positions.data?.filter((row) => row.status !== 'CLOSED') ?? []
  const closedSeats = positions.data?.filter((row) => row.status === 'CLOSED') ?? []

  return (
    <div className="space-y-4">
      <PageHeader
        title="Positions"
        breadcrumb={breadcrumb}
        subtitle="The seats, filled or otherwise — the headcount plan the org chart is drawn from."
        actions={
          <PermissionGate permission="workforce:write">
            <Button
              type="button" variant="outline"
              onClick={() => {
                void downloadFile(bulkPaths('/v1/org/positions').export, 'positions.xlsx')
              }}
            >
              <Download className="size-4" aria-hidden="true" />
              Export
            </Button>
            <Button type="button" variant="outline" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import
            </Button>
            <Button type="button" onClick={() => open()}>
              <Plus className="size-4" aria-hidden="true" />
              New position
            </Button>
          </PermissionGate>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="positions"
        prefix="/v1/org/positions"
        invalidateKey={['org']}
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A position is a seat, not a person. It becomes filled when somebody is assigned to it,
        and closing one is refused while it is occupied — an employment pointing at a seat the
        org chart says is not there is an error nobody sees until a headcount report is run.
      </p>

      {error && !draft && !removing && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>
      )}

      {positions.isPending && <Skeleton className="h-40" />}
      {positions.isError && (
        <ErrorState error={positions.error} onRetry={() => void positions.refetch()} />
      )}
      {positions.isSuccess && positions.data.length === 0 && (
        <EmptyState
          title="No positions yet"
          description="Add the seats you plan to fill, or import them from a spreadsheet."
        />
      )}

      {positions.isSuccess && positions.data.length > 0 && (
        <Card>
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Title</th>
                <th className="p-3 text-start font-medium">Department</th>
                <th className="p-3 text-start font-medium">Status</th>
                <th className="p-3 text-start font-medium">Held by</th>
                <th className="p-3 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {[...openSeats, ...closedSeats].map((row) => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="p-3 font-mono">{row.code}</td>
                  <td className={`p-3 ${row.status === 'CLOSED' ? 'text-slate-400 line-through' : ''}`}>
                    {row.title}
                    {row.keyPosition && (
                      <span className="ms-2 text-xs text-[var(--color-muted)]">Key position</span>
                    )}
                  </td>
                  <td className="p-3">{row.departmentName ?? '—'}</td>
                  <td className="p-3">
                    <StatusBadge
                      status={row.status}
                      tone={STATUS_TONE[row.status] ?? 'neutral'}
                    />
                  </td>
                  <td className="p-3">{row.occupantName ?? '—'}</td>
                  <td className="p-3 text-end">
                    <PermissionGate permission="workforce:write">
                      <Button
                        type="button" variant="ghost" size="sm"
                        aria-label={`Edit ${row.title}`}
                        onClick={() => open(row)}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                      {row.status !== 'CLOSED' && (
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => { setError(null); setRemoving(row) }}
                        >
                          Close
                        </Button>
                      )}
                    </PermissionGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(next) => { if (!next) setRemoving(null) }}
        title={`Close ${removing?.title ?? 'this position'}?`}
        description="It stops being offered and stays on file, so a requisition raised against it still resolves. It is never deleted."
        confirmLabel="Close the position"
        loading={deactivate.isPending}
        error={error}
        onConfirm={() => { void close() }}
      />

      <FormDialog
        open={draft !== null}
        onOpenChange={(next) => { if (!next) setDraft(null) }}
        title={draft?.id ? 'Edit position' : 'New position'}
        description="A seat in the plan. Filling it is a separate act — assigning somebody to it."
        size="lg"
        submitLabel="Save position"
        loading={save.isPending}
        error={error}
        onSubmit={() => { void submit() }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="position-code">Code</Label>
              <Input
                id="position-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="position-title">Title</Label>
              <Input
                id="position-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="position-department">Department</Label>
              <Select
                id="position-department" className="w-full" value={draft.departmentId}
                onChange={(event) => setDraft({ ...draft, departmentId: event.target.value })}
              >
                <option value="">Choose a department</option>
                {departments.data?.map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Required — a position with no department is in no headcount.
              </p>
            </div>
            <div>
              <Label htmlFor="position-designation">Designation</Label>
              <Select
                id="position-designation" className="w-full" value={draft.designationId}
                onChange={(event) => setDraft({ ...draft, designationId: event.target.value })}
              >
                <option value="">Not set</option>
                {designations.data?.map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="position-grade">Grade</Label>
              <Select
                id="position-grade" className="w-full" value={draft.gradeId}
                onChange={(event) => setDraft({ ...draft, gradeId: event.target.value })}
              >
                <option value="">Not set</option>
                {grades.data?.map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="position-location">Location</Label>
              <Select
                id="position-location" className="w-full" value={draft.locationId}
                onChange={(event) => setDraft({ ...draft, locationId: event.target.value })}
              >
                <option value="">Not set</option>
                {locations.data?.map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="position-reports-to">Reports to</Label>
              <Select
                id="position-reports-to" className="w-full" value={draft.reportsToPositionId}
                onChange={(event) =>
                  setDraft({ ...draft, reportsToPositionId: event.target.value })}
              >
                <option value="">Nothing above it</option>
                {positions.data
                  ?.filter((row) => row.id !== draft.id && row.status !== 'CLOSED')
                  .map((row) => (
                    <option key={row.id} value={row.id}>{row.title} ({row.code})</option>
                  ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="position-status">Status</Label>
              <Select
                id="position-status" className="w-full" value={draft.status}
                onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              >
                <option value="">Vacant</option>
                <option value="VACANT">Vacant</option>
                <option value="FROZEN">Frozen — not to be filled for now</option>
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Filled is not offered: a position becomes filled when somebody is assigned to
                it, not when a field is set.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.keyPosition}
                onChange={(event) => setDraft({ ...draft, keyPosition: event.target.checked })}
              />
              Key position — losing this seat is a business risk worth planning for.
            </label>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
